import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Bell, BellRing, BellOff, Send, Smartphone, Mail, MonitorSmartphone, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CHANNEL_META = {
  push: { label: "Push", icon: Smartphone },
  email: { label: "Email", icon: Mail },
  inapp: { label: "In-app", icon: MonitorSmartphone },
};

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const supportsPush = () =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export default function NotificationSettings() {
  const [config, setConfig] = useState(null);
  const [prefs, setPrefs] = useState({});
  const [permission, setPermission] = useState(supportsPush() ? Notification.permission : "unsupported");
  const [subscribing, setSubscribing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/push/config");
      setConfig(data);
      setPrefs(data.prefs || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      await api.put("/push/preferences", { prefs: next });
    } catch (e) {
      toast.error("Impossibile salvare le preferenze");
    }
  };

  const toggle = (cat, ch) => {
    const next = { ...prefs, [cat]: { ...(prefs[cat] || {}), [ch]: !(prefs[cat]?.[ch]) } };
    savePrefs(next);
  };

  const enablePush = async () => {
    if (!supportsPush()) { toast.error("Il tuo browser non supporta le notifiche push"); return; }
    setSubscribing(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") { toast.error("Permesso notifiche negato dal browser"); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.vapid_public_key),
      });
      const { data } = await api.post("/push/subscribe", { subscription: sub.toJSON() });
      setConfig((c) => ({ ...c, subscribed_count: data.subscribed_count }));
      toast.success("Notifiche push attivate su questo dispositivo");
    } catch (e) {
      toast.error("Attivazione push non riuscita: " + (e.message || ""));
    } finally {
      setSubscribing(false);
    }
  };

  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await api.post("/push/unsubscribe", { subscription: sub.toJSON() });
        await sub.unsubscribe();
      }
      setConfig((c) => ({ ...c, subscribed_count: 0 }));
      toast.success("Notifiche push disattivate su questo dispositivo");
    } catch (e) {
      toast.error("Operazione non riuscita");
    }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/push/test");
      if (data.sent > 0) toast.success("Notifica di prova inviata! Controlla il dispositivo.");
      else toast.warning("Nessun dispositivo iscritto. Attiva prima le push qui sopra.");
    } catch (e) {
      toast.error("Invio prova non riuscito");
    }
  };

  if (loading) return <div className="text-zinc-500 text-sm py-6" data-testid="notif-settings-loading">Caricamento impostazioni…</div>;

  const subscribed = (config?.subscribed_count || 0) > 0;
  const consentBadge =
    permission === "granted"
      ? { txt: "Consenso concesso", cls: "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10", Icon: CheckCircle2 }
      : permission === "denied"
      ? { txt: "Consenso negato (sblocca dai permessi del browser)", cls: "text-red-400 border-red-500/40 bg-red-500/10", Icon: AlertTriangle }
      : permission === "unsupported"
      ? { txt: "Browser non supportato", cls: "text-zinc-400 border-white/15 bg-white/5", Icon: AlertTriangle }
      : { txt: "Consenso non ancora richiesto", cls: "text-amber-400 border-amber-500/40 bg-amber-500/10", Icon: Bell };

  return (
    <div className="space-y-5" data-testid="notification-settings">
      {/* Stato push + azioni */}
      <div className="rounded-lg bg-[#0A0A0C] border border-white/10 p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <BellRing className="h-4 w-4 text-[#00E676]" /> Notifiche push su questo dispositivo
          </div>
          <span data-testid="push-consent-status" className={`text-[11px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${consentBadge.cls}`}>
            <consentBadge.Icon className="h-3 w-3" /> {consentBadge.txt}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!subscribed ? (
            <Button data-testid="enable-push-btn" onClick={enablePush} disabled={subscribing || permission === "unsupported"}
              className="bg-[#00E676] text-black rounded-full h-9 text-sm">
              <Bell className="h-4 w-4 mr-1.5" /> {subscribing ? "Attivazione…" : "Attiva notifiche push"}
            </Button>
          ) : (
            <Button data-testid="disable-push-btn" onClick={disablePush} variant="outline"
              className="rounded-full h-9 text-sm border-white/15 bg-transparent">
              <BellOff className="h-4 w-4 mr-1.5" /> Disattiva su questo dispositivo
            </Button>
          )}
          <Button data-testid="test-push-btn" onClick={sendTest} variant="outline"
            className="rounded-full h-9 text-sm border-[#00E676]/40 text-[#00E676] bg-transparent hover:bg-[#00E676]/10">
            <Send className="h-4 w-4 mr-1.5" /> Invia notifica di prova
          </Button>
        </div>
        <div className="text-[11px] text-zinc-500" data-testid="last-notification-at">
          Ultima notifica ricevuta:{" "}
          {config?.last_notification_at ? new Date(config.last_notification_at).toLocaleString("it-IT") : "nessuna ancora"}
        </div>
      </div>

      {/* Preferenze per categoria e canale */}
      <div className="space-y-2">
        <div className="flex items-center justify-end gap-6 px-1 text-[10px] uppercase tracking-widest text-zinc-500">
          {config.channels.map((ch) => {
            const M = CHANNEL_META[ch] || { label: ch, icon: Bell };
            return (
              <span key={ch} className="flex items-center gap-1 w-12 justify-center"><M.icon className="h-3 w-3" />{M.label}</span>
            );
          })}
        </div>
        {config.categories.map((cat) => (
          <div key={cat.key} data-testid={`notif-cat-${cat.key}`} className="rounded-lg bg-[#0A0A0C] border border-white/10 p-3 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium">{cat.label}</div>
              <div className="text-[11px] text-zinc-500 leading-snug">{cat.desc}</div>
            </div>
            <div className="flex items-center gap-6 shrink-0">
              {config.channels.map((ch) => (
                <div key={ch} className="w-12 flex justify-center">
                  <Switch data-testid={`pref-${cat.key}-${ch}`} checked={!!prefs[cat.key]?.[ch]} onCheckedChange={() => toggle(cat.key, ch)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
